import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@mui/styles";
import { Typography, IconButton } from "@mui/material";
import { generateClient } from "aws-amplify/api";
import { listContents, listSeries, listAlbums } from "../../graphql/queries";
import { deleteContent, deleteSeries, deleteSeason, deleteAlbum } from "../../graphql/mutations";
import { useNavigate } from "react-router-dom";
import CustomDataGrid from "../../components/CustomDataGrid";
import Box from "@mui/material/Box";
import moment from "moment";
import ActionsPopover from "../../components/actionsPopover";
import CreateContentModal from "../../components/Content/CreateContentModal";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { get } from "aws-amplify/api";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

const useStyles = makeStyles((theme) => ({
  dataGridContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  },
  seriesRow: {
    backgroundColor: theme.palette.grey[50],
  },
  seasonRow: {
    backgroundColor: theme.palette.grey[50],
  },
  albumRow: {
    backgroundColor: theme.palette.grey[50],
  },
  consistentTextColor: {
    color: '#45484d', 
  }
}));

const Content = () => {
  const navigate = useNavigate();
  const classes = useStyles();
  const client = generateClient();
  const [allContentItems, setAllContentItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const [providerData, setProviderData] = useState({});
  const [expandedItems, setExpandedItems] = useState({});
  
  useEffect(() => {
    fetchContent();
  }, [success]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      
      // Fetch all content items
      let allItems = [];
      let nextToken = null;
      
      do {
        const response = await client.graphql({
          query: listContents,
          variables: { limit: 10000 }
        });
  
        allItems = [...allItems, ...response.data.listContents.items];
        nextToken = response.data.listContents.nextToken;
      } while (nextToken);
      
      // Fetch all series
      const seriesResponse = await client.graphql({
        query: listSeries,
        variables: { limit: 1000 }
      });
      
      const allSeries = seriesResponse.data.listSeries.items || [];
      
      // Fetch all albums
      const albumsResponse = await client.graphql({
        query: listAlbums,
        variables: { limit: 1000 }
      });
      
      const allAlbums = albumsResponse.data.listAlbums.items || [];
      
      // Process content items and add series, seasons, and albums
      const processedItems = [];
      
      // Add series as rows
      for (const series of allSeries) {
        const seriesId = `series-${series.id}`;
        processedItems.push({
          id: seriesId,
          title: series.title,
          type: "Series",
          isParent: true,
          originalId: series.id,
          owner: series.owner,
          itemType: "series"
        });
        
        // Find seasons for this series
        const seasons = {};
        const seriesContent = allItems.filter(item => item.seriesId === series.id);
        
        for (const content of seriesContent) {
          if (content.seasonId && content.season) {
            if (!seasons[content.seasonId]) {
              seasons[content.seasonId] = {
                id: content.seasonId,
                title: content.season.title,
                seasonNumber: content.season.seasonNumber,
                seriesId: series.id,
                owner: content.season.owner || content.owner,
                episodes: []
              };
            }
            seasons[content.seasonId].episodes.push(content);
          }
        }
        
        // Add seasons as rows
        for (const seasonId in seasons) {
          const season = seasons[seasonId];
          const seasonRowId = `season-${seasonId}`;
          
          processedItems.push({
            id: seasonRowId,
            title: `${season.title}`,
            parent: seriesId,
            type: "Season",
            isParent: true,
            originalId: seasonId,
            owner: season.owner,
            itemType: "season",
            seriesId: series.id
          });
          
          // Add episodes as rows
          for (const episode of season.episodes) {
            processedItems.push({
              ...episode,
              id: `episode-${episode.id}`,
              parent: seasonRowId,
              grandParent: seriesId,
              isParent: false,
              originalId: episode.id,
              itemType: "episode"
            });
          }
        }
      }
      
      // Add albums as rows
      for (const album of allAlbums) {
        const albumId = `album-${album.id}`;
        processedItems.push({
          id: albumId,
          title: album.title,
          type: "Album",
          isParent: true,
          originalId: album.id,
          owner: album.owner,
          itemType: "album"
        });
        
        // Find content items (tracks) for this album
        const albumContent = allItems.filter(item => item.albumId === album.id);
        
        // Add album tracks as rows
        for (const track of albumContent) {
          processedItems.push({
            ...track,
            id: `track-${track.id}`,
            parent: albumId,
            isParent: false,
            originalId: track.id,
            itemType: "track"
          });
        }
      }
      
      // Add non-series, non-album content
      const regularContent = allItems.filter(item => (!item.seriesId || !item.seasonId) && !item.albumId);
      processedItems.push(...regularContent.map(item => ({
        ...item,
        id: item.id,
        isParent: false,
        originalId: item.id,
        itemType: "content"
      })));
      
      // Fetch provider data for Admin role
      if (user.role === "Admin") {
        const uniqueOwners = [...new Set(processedItems.filter(item => item.owner).map(item => item.owner))];
        
        const providers = await Promise.all(
          uniqueOwners.map(async (ownerId) => {
            try {
              const providerResponse = await fetchContentProvider(ownerId);
              return {
                id: ownerId,
                name: providerResponse?.["custom:companyName"] || "Unknown"
              };
            } catch (error) {
              console.error("Error fetching provider:", ownerId, error);
              return { id: ownerId, name: "Unknown" };
            }
          })
        );
  
        const providerMap = providers.reduce((acc, provider) => {
          acc[provider.id] = provider.name;
          return acc;
        }, {});
        
        setProviderData(providerMap);
      }
      
      // Initialize expanded state for all series, seasons, and albums - set to false (closed) by default
      const initialExpandedState = {};
      processedItems.forEach(item => {
        if (item.isParent) {
          initialExpandedState[item.id] = false; // Default collapsed
        }
      });
      
      setExpandedItems(initialExpandedState);
      setAllContentItems(processedItems);
    } catch (error) {
      console.error("Error fetching content:", error);
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  const fetchContentProvider = async (contentProviderId) => {
    try {
      const restOperation = get({
        apiName: "restapi123",
        path: "/getUserData",
        options: {
          queryParams: {
            username: contentProviderId,
          },
        },
      });
      const { body } = await restOperation.response;
      const response = await body.json();
      return response;
    } catch (err) {
      console.log(err, "error");
    }
  };

  // Helper function to calculate parent status based on children
  const calculateParentStatus = (itemId, itemType) => {
    let childItems = [];
    
    if (itemType === "series") {
      // Get all episodes under this series (can be nested under seasons)
      childItems = allContentItems.filter(item => 
        (item.grandParent === itemId || item.parent === itemId) && 
        (item.itemType === "episode" || item.itemType === "track")
      );
    } else if (itemType === "season") {
      // Get all episodes under this season
      childItems = allContentItems.filter(item => 
        item.parent === itemId && 
        item.itemType === "episode"
      );
    } else if (itemType === "album") {
      // Get all tracks under this album
      childItems = allContentItems.filter(item => 
        item.parent === itemId && 
        item.itemType === "track"
      );
    }
    
    if (childItems.length === 0) return "";
    
    // Check if all children have the same status
    const allStatuses = new Set(childItems.map(item => item.status).filter(Boolean));
    
    if (allStatuses.size === 0) return "";
    if (allStatuses.size === 1) return childItems[0].status; // All have the same status
    
    // If some are approved and some are not
    if (allStatuses.has("Approved")) {
      const approvedCount = childItems.filter(item => item.status === "Approved").length;
      return `Partially Approved (${approvedCount}/${childItems.length})`;
    }
    
    // Mixed statuses other than Approved
    return "Mixed Status";
  };
  
  // Calculate agreement terms for parent items based on children
  const calculateParentAgreement = (itemId, itemType) => {
    let childItems = [];
    
    if (itemType === "series") {
      childItems = allContentItems.filter(item => 
        (item.grandParent === itemId || item.parent === itemId) && 
        (item.itemType === "episode" || item.itemType === "track")
      );
    } else if (itemType === "season") {
      childItems = allContentItems.filter(item => 
        item.parent === itemId && 
        item.itemType === "episode"
      );
    } else if (itemType === "album") {
      childItems = allContentItems.filter(item => 
        item.parent === itemId && 
        item.itemType === "track"
      );
    }
    
    if (childItems.length === 0) return "";
    
    // Get unique agreement values
    const agreements = childItems
      .map(item => item.agreement?.daysOfAgreement)
      .filter(Boolean);
    
    if (agreements.length === 0) return "";
    
    // Check if all have the same agreement
    const uniqueAgreements = new Set(agreements);
    if (uniqueAgreements.size === 1) {
      return `${agreements[0] / 30} months`;
    }
    
    // If different agreements, show range
    const minAgreement = Math.min(...agreements);
    const maxAgreement = Math.max(...agreements);
    return `${minAgreement / 30}-${maxAgreement / 30} months`;
  };
  
  // Calculate dates for parent items based on children
  const calculateParentDates = (itemId, itemType, dateField) => {
    let childItems = [];
    
    if (itemType === "series") {
      childItems = allContentItems.filter(item => 
        (item.grandParent === itemId || item.parent === itemId) && 
        (item.itemType === "episode" || item.itemType === "track")
      );
    } else if (itemType === "season") {
      childItems = allContentItems.filter(item => 
        item.parent === itemId && 
        item.itemType === "episode"
      );
    } else if (itemType === "album") {
      childItems = allContentItems.filter(item => 
        item.parent === itemId && 
        item.itemType === "track"
      );
    }
    
    if (childItems.length === 0) return "";
    
    // Get valid dates
    const dates = childItems
      .map(item => item[dateField])
      .filter(Boolean)
      .map(date => new Date(date));
    
    if (dates.length === 0) return "";
    
    // Check if all have the same date
    const uniqueDatesStr = new Set(dates.map(d => d.toISOString().split('T')[0]));
    if (uniqueDatesStr.size === 1) {
      return moment(dates[0]).format("DD MMM YYYY");
    }
    
    // If different dates, return the latest
    const latestDate = new Date(Math.max(...dates));
    return `Latest: ${moment(latestDate).format("DD MMM YYYY")}`;
  };

  // Enrich parent rows with aggregated data from children
  const enrichedContentItems = useMemo(() => {
    return allContentItems.map(item => {
      if (item.isParent) {
        const status = calculateParentStatus(item.id, item.itemType);
        const agreement = calculateParentAgreement(item.id, item.itemType);
        const uploadRequestDate = calculateParentDates(item.id, item.itemType, 'createdAt');
        const uploadedDate = calculateParentDates(item.id, item.itemType, 'releaseDate');
        
        return {
          ...item,
          calculatedStatus: status,
          calculatedAgreement: agreement,
          calculatedUploadRequest: uploadRequestDate,
          calculatedUploaded: uploadedDate
        };
      }
      return item;
    });
  }, [allContentItems]);

  // Memoize the filtered rows for better performance
  const filteredRows = useMemo(() => {
    return enrichedContentItems.filter(row => {
      // Always show top-level rows and regular content
      if (!row.parent) {
        return true;
      }
      
      // For season rows, check if their parent series is expanded
      if (row.itemType === "season") {
        return expandedItems[row.parent] === true;
      }
      
      // For track rows, check if their parent album is expanded
      if (row.itemType === "track") {
        return expandedItems[row.parent] === true;
      }
      
      // For episode rows, check if both parent season and grandparent series are expanded
      if (row.itemType === "episode") {
        const seasonExpanded = expandedItems[row.parent] === true;
        const seriesExpanded = expandedItems[row.grandParent] === true;
        return seasonExpanded && seriesExpanded;
      }
      
      return true;
    });
  }, [enrichedContentItems, expandedItems]);

  const toggleExpand = (event, itemId) => {
    event.stopPropagation();
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const columns = [
    { 
      field: "title", 
      headerName: "Title", 
      flex: 1,
      renderCell: (params) => {
        if (params.row.isParent) {
          const isExpanded = expandedItems[params.row.id] === true;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton 
                size="small" 
                onClick={(e) => toggleExpand(e, params.row.id)}
                sx={{ padding: 0.5 }}
              >
                {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
              </IconButton>
              <Typography 
                sx={{ 
                  fontWeight: 'bold', 
                  pl: params.row.parent ? 2 : 0
                }}
              >
                {params.value}
              </Typography>
            </Box>
          );
        }
        
        return (
          <Typography 
            className={classes.consistentTextColor}
            sx={{ 
              pl: params.row.parent ? 6 : 0 
            }}
          >
            {params.value}
          </Typography>
        );
      }
    },
    { field: "type", headerName: "Category", flex: 1 },
    { field: "currentCycle", headerName: "Current Cycle" },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        if (params.row.isParent) {
          return (
            <div>
              {params.row.calculatedStatus || ""}
            </div>
          );
        }
        
        return (
          <div>
            {params?.value === "Feedback"
              ? (user.role === "Admin" && "Sent Feedback") ||
                (user.role === "ContentProviders" && "Received Feedback")
              : params?.value}
          </div>
        );
      },
    },
    {
      field: "agreement.daysOfAgreement",
      headerName: "Term Of Agreement",
      flex: 1,
      valueGetter: (params) => params.row.agreement?.daysOfAgreement,
      renderCell: (params) => {
        if (params.row.isParent) {
          return (
            <div>
              {params.row.calculatedAgreement || ""}
            </div>
          );
        }
        
        return <div>{params.value ? `${params.value / 30} months` : ''}</div>;
      },
    },
    {
      field: "createdAt",
      headerName: "Upload Request",
      flex: 1,
      renderCell: (params) => {
        if (params.row.isParent) {
          return (
            <div>
              {params.row.calculatedUploadRequest || ""}
            </div>
          );
        }
        
        return <div>{params?.value ? moment(params?.value).format("DD MMM YYYY") : ''}</div>;
      },
    },
    {
      field: "releaseDate",
      headerName: "Uploaded",
      flex: 1,
      renderCell: (params) => {
        if (params.row.isParent) {
          return (
            <div>
              {params.row.calculatedUploaded || ""}
            </div>
          );
        }
        
        return (
          <div>
            {params?.value
              ? moment(params?.value).format("DD MMM YYYY")
              : "Pending"}
          </div>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => {
        // For series
        if (params.row.itemType === "series") {
          return (
            <ActionsPopover
              itemId={params.row.originalId}
              canDelete={true}
              onDelete={() => deleteSeriesById(params.row.originalId)}
              // Removed viewPath and editPath props
              canEdit={false}
            />
          );
        }
        
        // For seasons
        if (params.row.itemType === "season") {
          return (
            <ActionsPopover
              itemId={params.row.originalId}
              canDelete={true}
              onDelete={() => deleteSeasonById(params.row.originalId)}
              // Removed viewPath and editPath props
              canEdit={false}
            />
          );
        }
        
        // For albums
        if (params.row.itemType === "album") {
          return (
            <ActionsPopover
              itemId={params.row.originalId}
              canDelete={true}
              onDelete={() => deleteAlbumById(params.row.originalId)}
              // Removed viewPath and editPath props
              canEdit={false}
            />
          );
        }
        
        // For episodes, tracks and regular content
        return (
          <ActionsPopover
            itemId={params.row.originalId}
            viewPath={`/content/${params.row.originalId}`}
            editPath={`/content/edit/${params.row.agreementId}/${params.row.originalId}`}
            canDelete={true}
            onDelete={() => deleteContentById(params.row.originalId)}
            canEdit={user.role === "ContentProviders"}
            canPushToManifest={
              user?.role === "Admin" && params?.row?.status === "Approved"
            }
          />
        );
      },
    },
  ];

  if (user?.role === "Admin") {
    const contentProviderColumn = {
      field: "owner",
      headerName: "Content Provider",
      flex: 1,
      renderCell: (params) => (
        <Typography>{params.row.owner ? (providerData[params.row.owner] || "Unknown") : ""}</Typography>
      ),
    };

    // Insert the column in the second last position
    columns.splice(columns.length - 1, 0, contentProviderColumn);
  }

  const deleteSeriesById = async (seriesId) => {
    try {
      // Delete the series
      const response = await client.graphql({
        query: deleteSeries,
        variables: { input: { id: seriesId } },
      });
      
      if (response.data.deleteSeries) {
        toast.success("Series and all related content deleted successfully!");
        setSuccess(!success);
      }
    } catch (err) {
      console.log("Error deleting Series:", err);
      toast.error(err.message);
    }
  };

  const deleteSeasonById = async (seasonId) => {
    try {
      // Delete the season
      const response = await client.graphql({
        query: deleteSeason,
        variables: { input: { id: seasonId } },
      });
      
      if (response.data.deleteSeason) {
        toast.success("Season and all episodes deleted successfully!");
        setSuccess(!success);
      }
    } catch (err) {
      console.log("Error deleting Season:", err);
      toast.error(err.message);
    }
  };

  const deleteAlbumById = async (albumId) => {
    try {
      // Delete the album
      const response = await client.graphql({
        query: deleteAlbum,
        variables: { input: { id: albumId } },
      });
      
      if (response.data.deleteAlbum) {
        toast.success("Album and all tracks deleted successfully!");
        setSuccess(!success);
      }
    } catch (err) {
      console.log("Error deleting Album:", err);
      toast.error(err.message);
    }
  };

  const deleteContentById = async (contentId) => {
    try {
      const response = await client.graphql({
        query: deleteContent,
        variables: { input: { id: contentId } },
      });
      if (response.data.deleteContent) {
        toast.success("Deleted Successfully!");
        setSuccess(!success);
      }
    } catch (err) {
      console.log("Error deleting Content:", err);
      toast.error(err.message);
    }
  };

  return (
    <>
      {user?.role === "ContentProviders" && <CreateContentModal />}
      <Box marginTop={2}>
        <div className={classes.dataGridContainer}>
          <CustomDataGrid
            loading={loading}
            slotProps={{
              loadingOverlay: {
                variant: "linear-progress",
                noRowsVariant: "skeleton",
              },
            }}
            rows={filteredRows}
            columns={columns}
            pageSize={10}
            getRowClassName={(params) => {
              if (params.row.itemType === "series") return classes.seriesRow;
              if (params.row.itemType === "season") return classes.seasonRow;
              if (params.row.itemType === "album") return classes.albumRow;
              return "";
            }}
            disableRowSelectionOnClick
          />
        </div>
      </Box>
    </>
  );
};

export default Content;